import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  InputBase,
  Stack,
  TextField,
  InputAdornment,
  Popover,
} from '@mui/material';
import GradientColorPickerLib from 'react-best-gradient-color-picker';
import styled from 'styled-components';
import {
  Palette as PaletteIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  InfoOutlined as InfoOutlinedIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { FormTheme } from '../../types';
import { ThemePreset, ThemePresetGrid } from './ThemePresets';
import { THEME_UI, ColorSection, LabeledText } from './themeEditorParts';
import { DEFAULT_THEME } from './defaultTheme';
import { FormStepper, getStepperPosition } from '../shared/FormStepper';
import { backgroundSx } from '../../utils/themeUtils';
import { copyToClipboard } from '../../utils/clipboard';
import {
  DEFAULT_FORM_WIDTH,
  FORM_WIDTH_LABELS,
  FORM_WIDTH_OPTIONS,
  FORM_WIDTH_PRESETS,
  MAX_CUSTOM_FORM_WIDTH,
  MIN_CUSTOM_FORM_WIDTH,
} from '../../utils/formWidth';

 
const GradientPickerWrapper = styled.div`
  .color_picker {
    padding: 6px !important;
    width: 100% !important;
    overflow: hidden;
    .rbgcp-color-picker-dark,
    .rbgcp-color-picker {
      width: 100% !important;
      padding: 0 !important;
    }
    [id^='rbgcp-square-wrapper'] {
      margin-bottom: 6px !important;
    }
    [id^='rbgcp-preview'] {
      display: none !important;
    }
    [id^='rbgcp-presets-wrapper'] {
      width: 100% !important;
    }
    [id^='rbgcp-footer-wrapper'] {
      margin-top: 8px !important;
    }
    #rbgcp-gradient-bar-canvas-dark {
      width: 100%;
    }
    canvas {
      max-width: 100% !important;
      width: 100% !important;
      height: auto !important;
      box-sizing: border-box;
      display: block;
      cursor: pointer;
    }
    [id^='rbgcp-presets-wrapper'] div {
      cursor: pointer;
    }
    [id^='rbgcp-control-btn-wrapper'],
    [id^='rbgcp-control-btn-wrapper'] *,
    .rbgcp-control-icon-btn,
    .rbgcp-control-input-wrap-mini {
      cursor: pointer;
    }
  }
`;

interface ThemeEditorProps {
  open: boolean;
  onClose: () => void;
  theme: FormTheme | null;
  onSave: (theme: FormTheme) => void;
  onChange?: (theme: FormTheme) => void;
  isMultiStep?: boolean;
}

const fonts = [
  { family: 'Outfit', weights: ['regular', '700'] },
  { family: 'Roboto', weights: ['regular', '700', '900'] },
  { family: 'Open Sans', weights: ['regular', '600', '700'] },
  { family: '"Llama Mad", sans-serif', weights: ['regular', '600', '700'] },
  { family: 'Arial, sans-serif', weights: ['regular', '600', '700'] },
];

const HEX_RE = /^#?[0-9a-fA-F]{0,6}$/;

const normalizeHex = (raw: string): string | null => {
  let v = raw.trim();
  if (!v) return null;
  if (!v.startsWith('#')) v = `#${v}`;
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  }
  return null;
};

 
const ColorPicker: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  allowGradient?: boolean;
  /** Short "what does this color paint" note, shown on the info icon. */
  hint?: string;
}> = ({ label, value, onChange, allowGradient = false, hint }) => {
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState(value);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isGradient = /gradient/i.test(value || '');

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await copyToClipboard(value.toUpperCase()))) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const commitDraft = () => {
    const normalized = normalizeHex(draft);
    if (normalized) {
      onChange(normalized);
      setDraft(normalized);
    } else {
      setDraft(value);
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          mb: 0.75,
          fontSize: '12px',
          fontWeight: 600,
          color: THEME_UI.textSecondary,
          letterSpacing: 0.1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {label}
        {hint && (
          <Tooltip title={hint}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 38,
          border: '1px solid',
          borderColor: THEME_UI.border,
          borderRadius: 2,
          backgroundColor: THEME_UI.fieldBg,
          pl: 0.75,
          pr: 0.5,
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': { borderColor: THEME_UI.borderStrong },
          '&:focus-within': {
            borderColor: THEME_UI.accent,
            boxShadow: `0 0 0 3px ${THEME_UI.ring}`,
          },
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1.5,
            // Inner white ring keeps light swatches legible against the field.
            border: '1px solid rgba(0,0,0,0.12)',
            boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,0.9)',
            background: value || '#ffffff',
            cursor: 'pointer',
            flexShrink: 0,
            alignSelf: 'center',
            p: 0,
            transition: 'transform 0.12s ease',
            '&:hover': { transform: 'scale(1.06)' },
          }}
        />
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: { sx: { mt: 0.5, borderRadius: 2, width: 260 } },
          }}
        >
          <Stack spacing={0.75} sx={{ p: 1, position: 'relative', pt: 3.5 }}>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => setAnchorEl(null)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                zIndex: 2,
                color: 'grey.600',
                '&:hover': { color: 'grey.900', bgcolor: 'grey.100' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <GradientPickerWrapper>
              <GradientColorPickerLib
                value={value || (allowGradient ? 'rgba(255,255,255,1)' : '#ffffff')}
                onChange={onChange}
                hideColorTypeBtns={!allowGradient}
                hideGradientType={!allowGradient}
                hideGradientAngle={!allowGradient}
                hideGradientStop={!allowGradient}
                hideGradientControls={!allowGradient}
                hideEyeDrop
                hideColorGuide
                height={120}
                className="color_picker"
              />
            </GradientPickerWrapper>
            <Button
              size="small"
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => setAnchorEl(null)}
              sx={{ textTransform: 'none' }}
            >
              Apply
            </Button>
          </Stack>
        </Popover>
        <InputBase
          value={isGradient ? 'Gradient' : draft.toUpperCase()}
          disabled={isGradient}
          onChange={(e) => {
            const next = e.target.value;
            if (HEX_RE.test(next)) setDraft(next);
          }}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'monospace',
            fontSize: '13px',
            '& input': { p: 0, height: 'auto', lineHeight: '24px' },
          }}
        />
        <Tooltip title={copied ? 'Copied!' : 'Copy'}>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ p: 0.5, color: copied ? 'success.main' : 'text.secondary' }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 15 }} />
            ) : (
              <CopyIcon sx={{ fontSize: 15 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const LabeledSelect: React.FC<{
  label: string;
  value: string;
  options: string[];
  optionLabels?: Record<string, string>;
  onChange: (value: string) => void;
}> = ({ label, value, options, optionLabels, onChange }) => (
  <Box>
    <Typography
      sx={{
        mb: 0.5,
        fontSize: '12px',
        fontWeight: 500,
        color: 'text.secondary',
        letterSpacing: 0.2,
      }}
    >
      {label}
    </Typography>
    <FormControl fullWidth size="small">
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          borderRadius: 2,
          height: 38,
          fontSize: 13,
          backgroundColor: THEME_UI.fieldBg,
          '& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME_UI.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME_UI.borderStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: THEME_UI.accent,
            borderWidth: 1,
          },
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {optionLabels?.[opt] ?? opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

const LabeledNumber: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  /** Short "what does this do" note shown under the field. */
  hint?: string;
  onChange: (value: number) => void;
}> = ({ label, value, min, max, unit = 'px', hint, onChange }) => {
  const [text, setText] = useState(String(value));

  useEffect(() => {
    setText(String(value));
  }, [value]);

  const commit = () => {
    const n = Number(text);
    const clamped = Number.isNaN(n) ? value : Math.min(max, Math.max(min, n));
    setText(String(clamped));
    if (clamped !== value) onChange(clamped);
  };

  const previewLive = (raw: string) => {
    setText(raw);
    const n = Number(raw);
    if (!Number.isNaN(n) && raw.trim() !== '') onChange(n);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          mb: 0.75,
          fontSize: '12px',
          fontWeight: 600,
          color: THEME_UI.textSecondary,
          letterSpacing: 0.1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {label}
        <Tooltip title={`${hint ? `${hint} · ` : ''}Allowed: ${min}–${max}${unit}`}>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
        </Tooltip>
      </Typography>
      <TextField
        type="number"
        size="small"
        fullWidth
        value={text}
        inputProps={{ min, max }}
        onChange={(e) => previewLive(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ '& p': { fontSize: 12, color: 'text.disabled' } }}>
              {unit}
            </InputAdornment>
          ),
          sx: {
            borderRadius: 2,
            height: 38,
            fontSize: 13,
            backgroundColor: THEME_UI.fieldBg,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME_UI.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME_UI.borderStrong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: THEME_UI.accent,
              borderWidth: 1,
            },
          },
        }}
      />
    </Box>
  );
};

const FontGroup: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: THEME_UI.border,
      borderRadius: 2.5,
      p: 2,
      backgroundColor: THEME_UI.fieldBg,
      height: '100%',
    }}
  >
    <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: THEME_UI.text, mb: 1.5 }}>
      {title}
    </Typography>
    {/* Family and weight sit side by side: stacked, each card grew tall with a
        lot of dead space and the tab read as a long thin column of selects. */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 2,
      }}
    >
      {children}
    </Box>
  </Box>
);

const ThemePreview: React.FC<{
  theme: FormTheme;
  /** Shows a sample step indicator, for the Steps tab. */
  showStepper?: boolean;
  onRemoveBackgroundImage?: () => void;
}> = ({ theme, showStepper = false, onRemoveBackgroundImage }) => {
  const headingFont = theme.fontFamily || 'Outfit';
  const bodyFont = theme.paragraphFontFamily || theme.fontFamily || 'Outfit';
  const buttonFont = theme.buttonFontFamily || theme.fontFamily || 'Outfit';

  const stepperPreviewPosition = getStepperPosition(theme);
  const stepperPreviewNode = (
    <FormStepper
      steps={['Details', 'Address', 'Confirm']}
      activeStep={1}
      theme={theme}
      compact
    />
  );
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: THEME_UI.border,
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(16,24,40,0.07)',
        position: { md: 'sticky' },
        top: 0,
      }}
    >
      {/* Browser-style chrome: the traffic lights and fake address pill frame
          the sheet as a real page, which is what makes the swatch changes read
          as a form rather than as a stack of coloured boxes. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1.125,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid',
          borderColor: THEME_UI.divider,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.625, flexShrink: 0 }}>
          {['#ff5f57', '#febc2e', '#28c840'].map((dot) => (
            <Box
              key={dot}
              sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: dot }}
            />
          ))}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: 20,
            px: 1,
            borderRadius: 10,
            bgcolor: '#eef1f5',
            display: 'flex',
            alignItems: 'center',
            fontSize: 10,
            color: 'text.disabled',
            fontFamily: 'monospace',
          }}
        >
          your-form.com
        </Box>
        <Typography
          sx={{
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          Live
        </Typography>
      </Box>
      {/* Grey gutter so the sheet's own background colour is visible as a
          distinct surface even when the theme background is white. */}
      <Box sx={{ bgcolor: '#f1f3f6', p: 1.75, position: 'relative' }}>
        {(theme.backgroundImageUrl || '').trim() && onRemoveBackgroundImage && (
          <Tooltip title="Remove background image">
            <IconButton
              size="small"
              onClick={onRemoveBackgroundImage}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 1,
                width: 26,
                height: 26,
                bgcolor: 'rgba(17,24,39,0.55)',
                color: '#fff',
                backdropFilter: 'blur(2px)',
                '&:hover': { bgcolor: 'rgba(17,24,39,0.75)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: stepperPreviewPosition === 'left' ? 'row' : 'column',
          alignItems:
            stepperPreviewPosition === 'left' ? 'flex-start' : 'stretch',
          gap: stepperPreviewPosition === 'left' ? 1.75 : 0,
        }}
      >
        {showStepper && stepperPreviewPosition === 'top' && (
          <Box sx={{ mb: 1.75 }}>{stepperPreviewNode}</Box>
        )}
        {showStepper && stepperPreviewPosition === 'left' && (
          <Box
            sx={{
              flex: '0 0 96px',
              py: 1,
              px: 1.25,
              borderRadius: `${Math.min(theme.borderRadius ?? 8, 14)}px`,
              ...backgroundSx(theme),
            }}
          >
            {stepperPreviewNode}
          </Box>
        )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          borderRadius: `${Math.min(theme.borderRadius ?? 8, 14)}px`,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(16,24,40,0.09)',
          py: `${Math.min((theme.formPaddingY ?? 32) / 2, 40)}px`,
          px: `${Math.min((theme.formPaddingX ?? 32) / 2, 40)}px`,
          ...backgroundSx(theme),
        }}
      >
        <Typography
          sx={{
            fontFamily: headingFont,
            fontWeight: theme.fontWeight === 'regular' ? 400 : theme.fontWeight || 700,
            color: theme.heading1 || '#1677ff',
            fontSize: 22,
            mb: 1,
          }}
        >
          Heading text
        </Typography>
        <Typography
          sx={{
            fontFamily: bodyFont,
            fontWeight:
              theme.paragraphFontWeight === 'regular'
                ? 400
                : theme.paragraphFontWeight || 400,
            color: theme.paragraph || '#000000',
            fontSize: 14,
            mb: 0.5,
          }}
        >
          This is a paragraph of body copy with an{' '}
          <span style={{ color: theme.link || '#1677ff', textDecoration: 'underline' }}>
            example link
          </span>
          .
        </Typography>
        <Typography
          sx={{
            fontFamily: bodyFont,
            color: theme.label || '#000000',
            fontSize: 13,
            fontWeight: 600,
            mt: 1.5,
            mb: 0.5,
          }}
        >
          Field label
        </Typography>
        <Box
          sx={{
            width: '100%',
            height: `${theme.formInputHeight ?? 42}px`,
            borderRadius: `${theme.borderRadius ?? 8}px`,
            border: `1px solid ${theme.formInputBorder || '#d1d5db'}`,
            bgcolor: theme.formInputBackground || '#ffffff',
            /* This proxy shows placeholder copy, so it previews that colour
               rather than the typed-text one. */
            color: theme.formInputPlaceholder || '#9ca3af',
            fontFamily: bodyFont,
            fontSize: theme.formInputFontSize ?? 14,
            display: 'flex',
            alignItems: 'center',
            px: 1.25,
            mb: 2,
          }}
        >
          Placeholder text…
        </Box>
        <Box
          sx={{
            width: '100%',
            height: `${theme.formTextareaHeight ?? 96}px`,
            borderRadius: `${theme.borderRadius ?? 8}px`,
            border: `1px solid ${theme.formInputBorder || '#d1d5db'}`,
            bgcolor: theme.formInputBackground || '#ffffff',
            color: theme.formInputPlaceholder || '#9ca3af',
            fontFamily: bodyFont,
            fontSize: theme.formInputFontSize ?? 14,
            px: 1.25,
            py: 1,
            mb: 2,
          }}
        >
          Multi-line text…
        </Box>
        <Stack direction="row" spacing={1.5}>
          {/* Visual proxies only — not real buttons, so they can't be
              focused/clicked and never show a focus/active outline. */}
          <Box
            component="div"
            role="presentation"
            sx={{
              flex: 1,
              textAlign: 'center',
              py: `${theme.buttonPaddingY ?? 10}px`,
              px: `${theme.buttonPaddingX ?? 20}px`,
              borderRadius: `${theme.borderRadius ?? 8}px`,
              border: `1px solid ${theme.actionButtonBorder || theme.actionButtonBackground || '#1677ff'}`,
              outline: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
              fontFamily: buttonFont,
              fontSize: `${theme.buttonFontSize ?? 16}px`,
              fontWeight: theme.buttonFontWeight === 'regular' ? 400 : theme.buttonFontWeight || 600,
              boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
              background: theme.actionButtonBackground || '#1677ff',
              color: theme.actionButtonText || '#ffffff',
            }}
          >
            Submit
          </Box>
          <Box
            component="div"
            role="presentation"
            sx={{
              flex: 1,
              textAlign: 'center',
              py: `${theme.buttonPaddingY ?? 10}px`,
              px: `${theme.buttonPaddingX ?? 20}px`,
              borderRadius: `${theme.borderRadius ?? 8}px`,
              border: `1px solid ${theme.basicButtonBorder || theme.basicButtonText || '#1677ff'}`,
              outline: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
              fontFamily: buttonFont,
              fontSize: `${theme.buttonFontSize ?? 16}px`,
              fontWeight: theme.buttonFontWeight === 'regular' ? 400 : theme.buttonFontWeight || 600,
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              background: theme.basicButtonBackground || '#add8e6',
              color: theme.basicButtonText || '#1677ff',
            }}
          >
            Back
          </Box>
        </Stack>
      </Box>

        {showStepper && stepperPreviewPosition === 'bottom' && (
          <Box sx={{ mt: 1.75 }}>{stepperPreviewNode}</Box>
        )}
      </Box>
      </Box>
    </Box>
  );
};

export const ThemeEditor: React.FC<ThemeEditorProps> = ({
  open,
  onClose,
  theme,
  onSave,
  onChange,
  isMultiStep = false,
}) => {
  const [formTheme, setFormTheme] = useState<FormTheme>(theme || DEFAULT_THEME);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFont, setSelectedFont] = useState(formTheme.fontFamily || 'Outfit');
  const [availableWeights, setAvailableWeights] = useState(
    fonts.find((f) => f.family === selectedFont)?.weights || ['regular', '700']
  );
  const [selectedParagraphFont, setSelectedParagraphFont] = useState(
    formTheme.paragraphFontFamily || 'Outfit'
  );
  const [availableParagraphWeights, setAvailableParagraphWeights] = useState(
    fonts.find((f) => f.family === selectedParagraphFont)?.weights || ['regular', '700']
  );
  const [selectedButtonFont, setSelectedButtonFont] = useState(
    formTheme.buttonFontFamily || 'Outfit'
  );
  const [availableButtonWeights, setAvailableButtonWeights] = useState(
    fonts.find((f) => f.family === selectedButtonFont)?.weights || ['regular', '700']
  );

  useEffect(() => {
    if (theme) {
      setFormTheme(theme);
      if (theme.fontFamily) {
        setSelectedFont(theme.fontFamily);
        const fontData = fonts.find((f) => f.family === theme.fontFamily);
        if (fontData) setAvailableWeights(fontData.weights);
      }
      if (theme.paragraphFontFamily) {
        setSelectedParagraphFont(theme.paragraphFontFamily);
        const fontData = fonts.find((f) => f.family === theme.paragraphFontFamily);
        if (fontData) setAvailableParagraphWeights(fontData.weights);
      }
      if (theme.buttonFontFamily) {
        setSelectedButtonFont(theme.buttonFontFamily);
        const fontData = fonts.find((f) => f.family === theme.buttonFontFamily);
        if (fontData) setAvailableButtonWeights(fontData.weights);
      }
    }
  }, [theme]);

  const handleChange = (field: keyof FormTheme, value: any) => {
    const updatedTheme = { ...formTheme, [field]: value };
    setFormTheme(updatedTheme);
    onChange?.(updatedTheme);
  };

  const handleFontFamilyChange = (value: string) => {
    setSelectedFont(value);
    const selectedFontData = fonts.find((f) => f.family === value);
    if (selectedFontData) {
      setAvailableWeights(selectedFontData.weights);
      const nextWeight =
        formTheme.fontWeight && selectedFontData.weights.includes(formTheme.fontWeight)
          ? formTheme.fontWeight
          : selectedFontData.weights[0];
      setFormTheme((prev) => {
        const updated = { ...prev, fontFamily: value, fontWeight: nextWeight };
        onChange?.(updated);
        return updated;
      });
    }
  };

  const handleParagraphFontFamilyChange = (value: string) => {
    setSelectedParagraphFont(value);
    const selectedFontData = fonts.find((f) => f.family === value);
    if (selectedFontData) {
      setAvailableParagraphWeights(selectedFontData.weights);
      const nextWeight =
        formTheme.paragraphFontWeight &&
        selectedFontData.weights.includes(formTheme.paragraphFontWeight)
          ? formTheme.paragraphFontWeight
          : selectedFontData.weights[0];
      setFormTheme((prev) => {
        const updated = {
          ...prev,
          paragraphFontFamily: value,
          paragraphFontWeight: nextWeight,
        };
        onChange?.(updated);
        return updated;
      });
    }
  };

  const handleButtonFontFamilyChange = (value: string) => {
    setSelectedButtonFont(value);
    const selectedFontData = fonts.find((f) => f.family === value);
    if (selectedFontData) {
      setAvailableButtonWeights(selectedFontData.weights);
      const nextWeight =
        formTheme.buttonFontWeight && selectedFontData.weights.includes(formTheme.buttonFontWeight)
          ? formTheme.buttonFontWeight
          : selectedFontData.weights[0];
      setFormTheme((prev) => {
        const updated = { ...prev, buttonFontFamily: value, buttonFontWeight: nextWeight };
        onChange?.(updated);
        return updated;
      });
    }
  };

  const applyPreset = (preset: ThemePreset) => {
    setFormTheme((prev) => {
      const updated = {
        ...prev,
        backgroundImageUrl: undefined,
        backgroundImageSize: undefined,
        ...preset.theme,
        presetId: preset.id,
      };
      onChange?.(updated);
      return updated;
    });
  };

  const handleReset = () => {
    setFormTheme(DEFAULT_THEME);
    setSelectedFont(DEFAULT_THEME.fontFamily!);
    setAvailableWeights(fonts.find((f) => f.family === DEFAULT_THEME.fontFamily)?.weights || []);
    setSelectedParagraphFont(DEFAULT_THEME.paragraphFontFamily!);
    setAvailableParagraphWeights(
      fonts.find((f) => f.family === DEFAULT_THEME.paragraphFontFamily)?.weights || []
    );
    setSelectedButtonFont(DEFAULT_THEME.buttonFontFamily!);
    setAvailableButtonWeights(
      fonts.find((f) => f.family === DEFAULT_THEME.buttonFontFamily)?.weights || []
    );
    onChange?.(DEFAULT_THEME);
  };

  const handleSave = () => {
    onSave(formTheme);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            width: '100%',
            maxWidth: 1280,
            height: '88vh',
            maxHeight: 760,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(16,24,40,0.22)',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid',
          borderColor: THEME_UI.divider,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: THEME_UI.accentSoft,
              color: THEME_UI.accent,
            }}
          >
            <PaletteIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.25 }}>
              Theme
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Colors, typography and spacing for this form
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            width: 32,
            height: 32,
            color: 'text.secondary',
            '&:hover': { bgcolor: THEME_UI.hover, color: 'text.primary' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{
          px: 2.5,
          minHeight: 42,
          borderBottom: '1px solid',
          borderColor: THEME_UI.divider,
          '& .MuiTab-root': {
            minHeight: 42,
            px: 1.5,
            mr: 1,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
            '&.Mui-selected': { color: THEME_UI.accent },
          },
          '& .MuiTabs-indicator': {
            height: 2,
            borderRadius: '2px 2px 0 0',
            backgroundColor: THEME_UI.accent,
          },
        }}
      >
        <Tab label="Presets" />
        <Tab label="Styles" />
        <Tab label="Fonts" />
        <Tab label="Background" />
        {isMultiStep && <Tab label="Steps" />}
      </Tabs>

      <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Grid container sx={{ height: '100%', flexWrap: { md: 'nowrap' } }}>
          <Grid
            size={{ xs: 12, md: 7 }}
            sx={{
              p: 2.5,
              height: '100%',
              overflowY: 'auto',
              // Tinted so the white section cards read as raised surfaces.
              backgroundColor: THEME_UI.sectionBg,
              borderRight: { md: '1px solid' },
              borderColor: { md: THEME_UI.divider },
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: THEME_UI.scrollbar,
                borderRadius: 8,
                border: '2px solid transparent',
                backgroundClip: 'content-box',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: THEME_UI.scrollbarHover,
              },
              scrollbarWidth: 'thin',
            }}
          >
            {activeTab === 0 && (
              <ThemePresetGrid onSelect={applyPreset} selectedPresetId={formTheme.presetId} />
            )}

            {activeTab === 1 && (
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                  Every change applies to the whole form and shows up live in the
                  preview on the right.
                </Typography>

                <ColorSection
                  title="Page"
                  description="The sheet the form sits on, and how much empty space surrounds the fields."
                >
                  <ColorPicker
                    allowGradient
                    label="Background"
                    hint="Fill behind the whole form. Supports a gradient."
                    value={formTheme.background || '#ffffff'}
                    onChange={(value) => handleChange('background', value)}
                  />
                  <LabeledSelect
                    label="Form Width"
                    value={formTheme.formWidth || DEFAULT_FORM_WIDTH}
                    options={FORM_WIDTH_OPTIONS}
                    optionLabels={FORM_WIDTH_LABELS}
                    onChange={(value) => handleChange('formWidth', value as FormTheme['formWidth'])}
                  />
                  {formTheme.formWidth === 'custom' && (
                    <LabeledNumber
                      label="Custom Width"
                      hint="Maximum width of the form. Smaller screens shrink it automatically."
                      value={formTheme.formMaxWidth ?? FORM_WIDTH_PRESETS.medium}
                      min={MIN_CUSTOM_FORM_WIDTH}
                      max={MAX_CUSTOM_FORM_WIDTH}
                      onChange={(value) => handleChange('formMaxWidth', value)}
                    />
                  )}
                  <LabeledNumber
                    label="Space Top & Bottom"
                    hint="Gap above the first field and below the last one. 0 = flush."
                    value={formTheme.formPaddingY ?? 32}
                    min={0}
                    max={120}
                    onChange={(value) => handleChange('formPaddingY', value)}
                  />
                  <LabeledNumber
                    label="Space Left & Right"
                    hint="Gap between the fields and the left/right edges. 0 = full width."
                    value={formTheme.formPaddingX ?? 32}
                    min={0}
                    max={120}
                    onChange={(value) => handleChange('formPaddingX', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Headings"
                  description="Header Text blocks you drop into the form."
                >
                  <ColorPicker
                    label="Heading Text"
                    hint="Color of every heading (H1–H6) in the form."
                    value={formTheme.heading1 || '#1677ff'}
                    onChange={(value) => handleChange('heading1', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Text & Labels"
                  description="Body copy, links, and the label printed above each field."
                >
                  <ColorPicker
                    label="Paragraph"
                    hint="Plain text blocks and helper text."
                    value={formTheme.paragraph || '#000000'}
                    onChange={(value) => handleChange('paragraph', value)}
                  />
                  <ColorPicker
                    label="Link"
                    hint="Clickable links inside text blocks."
                    value={formTheme.link || '#1677ff'}
                    onChange={(value) => handleChange('link', value)}
                  />
                  <ColorPicker
                    label="Field Label"
                    hint="The caption above each input, e.g. “Email address”."
                    value={formTheme.label || '#000000'}
                    onChange={(value) => handleChange('label', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Input Fields"
                  description="Text boxes, dropdowns, and text areas users type into."
                >
                  <ColorPicker
                    label="Typed Text"
                    hint="Color of what the user types into a field."
                    value={formTheme.formInputText || '#808080'}
                    onChange={(value) => handleChange('formInputText', value)}
                  />
                  <ColorPicker
                    label="Placeholder Text"
                    hint="Color of the hint text shown while a field is empty."
                    value={formTheme.formInputPlaceholder || '#9ca3af'}
                    onChange={(value) => handleChange('formInputPlaceholder', value)}
                  />
                  <ColorPicker
                    label="Field Background"
                    hint="Fill inside the input box."
                    value={formTheme.formInputBackground || '#ffffff'}
                    onChange={(value) => handleChange('formInputBackground', value)}
                  />
                  <ColorPicker
                    label="Field Border"
                    hint="Outline around the input box."
                    value={formTheme.formInputBorder || '#d1d5db'}
                    onChange={(value) => handleChange('formInputBorder', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Input Size"
                  description="How tall and how rounded the input boxes are."
                >
                  <LabeledNumber
                    label="Field Height"
                    hint="Height of single-line inputs and dropdowns."
                    value={formTheme.formInputHeight ?? 42}
                    min={28}
                    max={80}
                    onChange={(value) => handleChange('formInputHeight', value)}
                  />
                  <LabeledNumber
                    label="Field Text Size"
                    hint="Size of the text and placeholder inside every input."
                    value={formTheme.formInputFontSize ?? 14}
                    min={11}
                    max={24}
                    onChange={(value) => handleChange('formInputFontSize', value)}
                  />
                  <LabeledNumber
                    label="Text Area Height"
                    hint="Starting height of multi-line inputs. Users can still drag them taller."
                    value={formTheme.formTextareaHeight ?? 96}
                    min={42}
                    max={400}
                    onChange={(value) => handleChange('formTextareaHeight', value)}
                  />
                  <LabeledNumber
                    label="Corner Radius"
                    hint="Roundness of inputs and buttons. 0 = square corners."
                    value={formTheme.borderRadius ?? 8}
                    min={0}
                    max={40}
                    onChange={(value) => handleChange('borderRadius', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Primary Button"
                  description="The main call-to-action, e.g. Submit or Pay Now."
                >
                  <ColorPicker
                    label="Text"
                    hint="Label color on the primary button."
                    value={formTheme.actionButtonText || '#ffffff'}
                    onChange={(value) => handleChange('actionButtonText', value)}
                  />
                  <ColorPicker
                    allowGradient
                    label="Background"
                    hint="Fill of the primary button. Supports a gradient."
                    value={formTheme.actionButtonBackground || '#1677ff'}
                    onChange={(value) => handleChange('actionButtonBackground', value)}
                  />
                  <ColorPicker
                    label="Border"
                    hint="Outline of the primary button. Match the background to hide it."
                    value={formTheme.actionButtonBorder || formTheme.actionButtonBackground || '#1677ff'}
                    onChange={(value) => handleChange('actionButtonBorder', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Secondary Button"
                  description="Supporting buttons, e.g. Back or Cancel on multi-step forms."
                >
                  <ColorPicker
                    label="Text"
                    hint="Label color on secondary buttons."
                    value={formTheme.basicButtonText || '#1677ff'}
                    onChange={(value) => handleChange('basicButtonText', value)}
                  />
                  <ColorPicker
                    allowGradient
                    label="Background"
                    hint="Fill of secondary buttons. Supports a gradient."
                    value={formTheme.basicButtonBackground || '#add8e6'}
                    onChange={(value) => handleChange('basicButtonBackground', value)}
                  />
                  <ColorPicker
                    label="Border"
                    hint="Outline of secondary buttons."
                    value={formTheme.basicButtonBorder || formTheme.basicButtonText || '#1677ff'}
                    onChange={(value) => handleChange('basicButtonBorder', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Button Size"
                  description="Applies to both primary and secondary buttons."
                >
                  <LabeledNumber
                    label="Text Size"
                    hint="Font size of the button label."
                    value={formTheme.buttonFontSize ?? 16}
                    min={10}
                    max={32}
                    onChange={(value) => handleChange('buttonFontSize', value)}
                  />
                  <LabeledNumber
                    label="Space Top & Bottom"
                    hint="Padding inside the button — makes it taller."
                    value={formTheme.buttonPaddingY ?? 10}
                    min={0}
                    max={40}
                    onChange={(value) => handleChange('buttonPaddingY', value)}
                  />
                  <LabeledNumber
                    label="Space Left & Right"
                    hint="Padding inside the button — makes it wider."
                    value={formTheme.buttonPaddingX ?? 20}
                    min={0}
                    max={60}
                    onChange={(value) => handleChange('buttonPaddingX', value)}
                  />
                </ColorSection>

                <ColorSection
                  title="Chip Select Colors"
                  description="Selectable chips, in their resting and chosen states."
                >
                  <ColorPicker
                    label="Background"
                    hint="Fill of an unselected chip."
                    value={formTheme.chipBackground || '#ffffff'}
                    onChange={(value) => handleChange('chipBackground', value)}
                  />
                  <ColorPicker
                    label="Text"
                    hint="Label color of an unselected chip."
                    value={formTheme.chipText || '#374151'}
                    onChange={(value) => handleChange('chipText', value)}
                  />
                  <ColorPicker
                    label="Border"
                    hint="Outline of an unselected chip."
                    value={formTheme.chipBorder || '#e5e7eb'}
                    onChange={(value) => handleChange('chipBorder', value)}
                  />
                  <ColorPicker
                    label="Hover Background"
                    hint="Fill while the pointer is over an unselected chip."
                    value={formTheme.chipHoverBackground || '#ffffff'}
                    onChange={(value) =>
                      handleChange('chipHoverBackground', value)
                    }
                  />
                  <ColorPicker
                    label="Hover Border"
                    hint="Outline while the pointer is over an unselected chip."
                    value={
                      formTheme.chipHoverBorder || '#c4b5fd'
                    }
                    onChange={(value) => handleChange('chipHoverBorder', value)}
                  />
                  <ColorPicker
                    label="Selected Background"
                    hint="Fill of the chosen chip."
                    value={formTheme.chipSelectedBackground || '#ffffff'}
                    onChange={(value) =>
                      handleChange('chipSelectedBackground', value)
                    }
                  />
                  <ColorPicker
                    label="Selected Text"
                    hint="Label color of the chosen chip."
                    value={formTheme.chipSelectedText || '#7c3aed'}
                    onChange={(value) =>
                      handleChange('chipSelectedText', value)
                    }
                  />
                  <ColorPicker
                    label="Selected Border"
                    hint="Outline of the chosen chip."
                    value={formTheme.chipSelectedBorder || '#8b5cf6'}
                    onChange={(value) =>
                      handleChange('chipSelectedBorder', value)
                    }
                  />
                </ColorSection>

                <ColorSection
                  title="Chip Select Size"
                  description="Size of the selectable chips in a Chip Select field."
                >
                  <LabeledNumber
                    label="Text Size"
                    hint="Font size of the chip label."
                    value={formTheme.chipFontSize ?? 14}
                    min={10}
                    max={32}
                    onChange={(value) => handleChange('chipFontSize', value)}
                  />
                  <LabeledSelect
                    label="Font Weight"
                    value={formTheme.chipFontWeight || 'regular'}
                    options={['regular', '600', '700']}
                    onChange={(value) => handleChange('chipFontWeight', value)}
                  />
                  <LabeledNumber
                    label="Space Top & Bottom"
                    hint="Padding inside the chip — makes it taller."
                    value={formTheme.chipPaddingY ?? 6}
                    min={0}
                    max={40}
                    onChange={(value) => handleChange('chipPaddingY', value)}
                  />
                  <LabeledNumber
                    label="Space Left & Right"
                    hint="Padding inside the chip — makes it wider."
                    value={formTheme.chipPaddingX ?? 10}
                    min={0}
                    max={60}
                    onChange={(value) => handleChange('chipPaddingX', value)}
                  />
                  <LabeledNumber
                    label="Corner Radius"
                    hint="Roundness of the chip's corners."
                    value={formTheme.chipRadius ?? formTheme.borderRadius ?? 8}
                    min={0}
                    max={40}
                    onChange={(value) => handleChange('chipRadius', value)}
                  />
                </ColorSection>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}>
                  There are many Google fonts to choose from — head over to Google to check
                  out their{' '}
                  <a href="https://fonts.google.com/" target="_blank" rel="noopener noreferrer">
                    latest fonts
                  </a>
                  .
                </Typography>

                {/* Plain stack of full-width cards — FontGroup lays its own
                    children out in a responsive grid, so the extra Grid
                    wrappers that used to sit here would fight it. */}
                <Stack spacing={1.5}>
                  <FontGroup title="Heading">
                    <LabeledSelect
                      label="Font Family"
                      value={selectedFont}
                      options={fonts.map((f) => f.family)}
                      onChange={handleFontFamilyChange}
                    />
                    <LabeledSelect
                      label="Font Weight"
                      value={formTheme.fontWeight || '700'}
                      options={availableWeights}
                      onChange={(v) => handleChange('fontWeight', v)}
                    />
                  </FontGroup>

                  <FontGroup title="Paragraph">
                    <LabeledSelect
                      label="Font Family"
                      value={selectedParagraphFont}
                      options={fonts.map((f) => f.family)}
                      onChange={handleParagraphFontFamilyChange}
                    />
                    <LabeledSelect
                      label="Font Weight"
                      value={formTheme.paragraphFontWeight || 'regular'}
                      options={availableParagraphWeights}
                      onChange={(v) => handleChange('paragraphFontWeight', v)}
                    />
                  </FontGroup>

                  <FontGroup title="Button">
                    <LabeledSelect
                      label="Font Family"
                      value={selectedButtonFont}
                      options={fonts.map((f) => f.family)}
                      onChange={handleButtonFontFamilyChange}
                    />
                    <LabeledSelect
                      label="Font Weight"
                      value={formTheme.buttonFontWeight || '700'}
                      options={availableButtonWeights}
                      onChange={(v) => handleChange('buttonFontWeight', v)}
                    />
                  </FontGroup>
                </Stack>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  Paste a link to an image already hosted elsewhere — there is no upload
                  here. The background colour or gradient on the Styles tab still shows
                  through any transparent part of the image, and stays as the fill if this
                  is left empty.
                </Typography>
<Box sx={{mt:"20px"}}>
                <ColorSection
                  title="Background image"
                  description="Applied to the sheet the form sits on, same as the Background colour on the Styles tab."
                >
                  <LabeledText
                    label="Image URL"
                    placeholder="https://example.com/background.jpg"
                    value={formTheme.backgroundImageUrl || ''}
                    onChange={(value) => handleChange('backgroundImageUrl', value)}
                  />
                  <LabeledSelect
                    label="Fit"
                    value={formTheme.backgroundImageSize || 'cover'}
                    options={['cover', 'contain', 'auto']}
                    onChange={(value) =>
                      handleChange('backgroundImageSize', value as FormTheme['backgroundImageSize'])
                    }
                  />
                </ColorSection>

                {(formTheme.backgroundImageUrl || '').trim() && (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: THEME_UI.border,
                        overflow: 'hidden',
                        height: 160,
                        ...backgroundSx(formTheme),
                      }}
                    />
                    <Tooltip title="Remove background image">
                      <IconButton
                        size="small"
                        onClick={() => handleChange('backgroundImageUrl', '')}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 28,
                          height: 28,
                          bgcolor: 'rgba(17,24,39,0.55)',
                          color: '#fff',
                          backdropFilter: 'blur(2px)',
                          '&:hover': { bgcolor: 'rgba(17,24,39,0.75)' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              </Box>
            )}

            {isMultiStep && activeTab === 4 && (
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}>
                  How the step indicator looks and where it sits. These only
                  apply to multi-step forms.
                </Typography>

                <ColorSection
                  title="Style"
                  description="The shape the progress through the form takes."
                >
                  <LabeledSelect
                    label="Indicator"
                    value={formTheme.stepperVariant || 'numbered'}
                    options={['numbered', 'progress', 'dots', 'tabs', 'sections']}
                    optionLabels={{
                      numbered: 'Numbered circles',
                      progress: 'Progress bar',
                      dots: 'Dots',
                      tabs: 'Tabs',
                      sections: 'Section bars (quiz)',
                    }}
                    onChange={(value) =>
                      handleChange(
                        'stepperVariant',
                        value as FormTheme['stepperVariant'],
                      )
                    }
                  />
                  <LabeledSelect
                    label="Position"
                    value={formTheme.stepperPosition || 'top'}
                    options={['top', 'bottom', 'left', 'hidden']}
                    optionLabels={{
                      top: 'Above the form',
                      bottom: 'Below the form',
                      left: 'Beside the form (vertical)',
                      hidden: 'Hidden',
                    }}
                    onChange={(value) =>
                      handleChange(
                        'stepperPosition',
                        value as FormTheme['stepperPosition'],
                      )
                    }
                  />
                </ColorSection>

                <ColorSection
                  title="Colors"
                  description="Marker colours for where the user is, has been, and has yet to go."
                >
                  <ColorPicker
                    label="Current Step"
                    hint="Marker for the step being filled in."
                    value={formTheme.stepperActiveColor || '#4f46e5'}
                    onChange={(value) => handleChange('stepperActiveColor', value)}
                  />
                  <ColorPicker
                    label="Completed Steps"
                    hint="Marker for steps already finished."
                    value={
                      formTheme.stepperCompletedColor ||
                      formTheme.stepperActiveColor ||
                      '#4f46e5'
                    }
                    onChange={(value) => handleChange('stepperCompletedColor', value)}
                  />
                  <ColorPicker
                    label="Upcoming Steps"
                    hint="Marker for steps not yet reached."
                    value={formTheme.stepperInactiveColor || '#c7c7d1'}
                    onChange={(value) => handleChange('stepperInactiveColor', value)}
                  />
                  <ColorPicker
                    label="Step Labels"
                    hint="Colour of the step names."
                    value={formTheme.stepperLabelColor || '#6b7280'}
                    onChange={(value) => handleChange('stepperLabelColor', value)}
                  />
                </ColorSection>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <input
                    id="stepperHideLabels"
                    type="checkbox"
                    checked={Boolean(formTheme.stepperHideLabels)}
                    onChange={(e) =>
                      handleChange('stepperHideLabels', e.target.checked)
                    }
                    style={{
                      width: 16,
                      height: 16,
                      margin: 0,
                      accentColor: THEME_UI.accent,
                      cursor: 'pointer',
                    }}
                  />
                  <Typography
                    component="label"
                    htmlFor="stepperHideLabels"
                    sx={{ fontSize: 13, color: THEME_UI.textSecondary, cursor: 'pointer' }}
                  >
                    Hide step names, show markers only
                  </Typography>
                </Box>
              </Box>
            )}

          </Grid>

          {/* Right: live preview (fixed, does not scroll with the controls) */}
          <Grid
            size={{ xs: 12, md: 5 }}
            sx={{
              p: 2.5,
              height: '100%',
              overflowY: 'auto',
              bgcolor: THEME_UI.fieldBg,
            }}
          >
            <ThemePreview
              theme={formTheme}
              showStepper={isMultiStep && activeTab === 4}
              onRemoveBackgroundImage={() => handleChange('backgroundImageUrl', '')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: '1px solid',
          borderColor: THEME_UI.divider,
          backgroundColor: THEME_UI.sectionBg,
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={handleReset}
          color="inherit"
          startIcon={<RestartAltIcon sx={{ fontSize: 17 }} />}
          sx={{
            height: 36,
            px: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13.5,
            color: 'text.secondary',
            borderRadius: 2,
            '&:hover': { bgcolor: THEME_UI.hover, color: 'text.primary' },
          }}
        >
          Reset to defaults
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              height: 36,
              px: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13.5,
              color: 'text.primary',
              border: '1px solid',
              borderColor: THEME_UI.border,
              borderRadius: 2,
              backgroundColor: THEME_UI.fieldBg,
              '&:hover': { bgcolor: THEME_UI.hover, borderColor: THEME_UI.borderStrong },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disableElevation
            sx={{
              height: 36,
              px: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13.5,
              borderRadius: 2,
              backgroundColor: THEME_UI.accent,
              boxShadow: '0 1px 2px rgba(16,24,40,0.1)',
              '&:hover': {
                backgroundColor: THEME_UI.accentHover,
                boxShadow: `0 2px 6px ${THEME_UI.ring}`,
              },
            }}
          >
            Apply Theme
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
